import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import {
  ArrowRight as ArrowRightIcon,
  Pencil as PencilIcon,
  Trash2 as TrashIcon,
  Calendar as CalendarIcon,
  Users as UserGroupIcon,
  FileText as DocumentTextIcon,
  CheckCircle as CheckCircleIcon,
  Clock as ClockIcon,
  List as ListBulletIcon,
  Briefcase as BriefcaseIcon,
  Link as LinkIcon
} from 'lucide-react';

const MeetingViewer = () => {
  const navigate = useNavigate();
  const { meetingId } = useParams();
  const { currentUser, hasPermission } = useAuth();
  const { meetings, users, departments, tasks, projects, deleteMeeting } = useData();

  const [meeting, setMeeting] = useState(null);
  const [activeTab, setActiveTab] = useState('summary'); // summary, tasks, projects

  const canEdit = hasPermission('deputy_ceo');

  useEffect(() => {
    const foundMeeting = meetings.find(m => m.id === meetingId);
    if (foundMeeting) {
      setMeeting(foundMeeting);
    }
  }, [meetingId, meetings]);

  if (!meeting) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">הישיבה לא נמצאה</h2>
            <button
              onClick={() => navigate('/meetings')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              חזרה לרשימת ישיבות
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || user?.email || 'לא ידוע';
  };

  // Get department name by ID
  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || 'לא ידוע';
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    return new Intl.DateTimeFormat('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  };

  // Calculate meeting duration
  const getMeetingDuration = () => {
    if (!meeting.startDate || !meeting.endDate) return null;
    const start = meeting.startDate?.toDate ? meeting.startDate.toDate() : new Date(meeting.startDate);
    const end = meeting.endDate?.toDate ? meeting.endDate.toDate() : new Date(meeting.endDate);
    const durationMs = end - start;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours} שעות ו-${minutes} דקות`;
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את הישיבה? פעולה זו לא ניתנת לביטול.')) {
      return;
    }

    try {
      await deleteMeeting(meetingId);
      alert('הישיבה נמחקה בהצלחה');
      navigate('/meetings');
    } catch (error) {
      console.error('Error deleting meeting:', error);
      alert('שגיאה במחיקת הישיבה');
    }
  };

  // Get created tasks
  const getCreatedTasks = () => {
    if (!meeting.createdTaskIds || meeting.createdTaskIds.length === 0) return [];
    return tasks.filter(task => meeting.createdTaskIds.includes(task.id));
  };

  // Get created projects
  const getCreatedProjects = () => {
    if (!meeting.createdProjectIds || meeting.createdProjectIds.length === 0) return [];
    return projects.filter(project => meeting.createdProjectIds.includes(project.id));
  };

  // Get linked tasks
  const getLinkedTasks = () => {
    if (!meeting.linkedTaskIds || meeting.linkedTaskIds.length === 0) return [];
    return tasks.filter(task => meeting.linkedTaskIds.includes(task.id));
  };

  // Get linked projects
  const getLinkedProjects = () => {
    if (!meeting.linkedProjectIds || meeting.linkedProjectIds.length === 0) return [];
    return projects.filter(project => meeting.linkedProjectIds.includes(project.id));
  };

  const createdTasks = getCreatedTasks();
  const createdProjects = getCreatedProjects();
  const linkedTasks = getLinkedTasks();
  const linkedProjects = getLinkedProjects();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4 flex-1">
              <button
                onClick={() => navigate('/meetings')}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                title="חזרה לרשימת ישיבות"
              >
                <ArrowRightIcon className="w-6 h-6" />
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-gray-900">{meeting.subject}</h1>
                  {meeting.status === 'completed' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <CheckCircleIcon className="w-4 h-4 ml-1" />
                      הסתיימה
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      <ClockIcon className="w-4 h-4 ml-1" />
                      בתהליך
                    </span>
                  )}
                </div>
                <p className="text-gray-600 mt-1">{meeting.purpose}</p>
              </div>
            </div>
            
            {/* Action Buttons - Always visible for authorized users */}
            {canEdit && (
              <div className="flex gap-2">
                {meeting.status !== 'completed' && (
                  <button
                    onClick={() => navigate(`/meetings/edit/${meetingId}`)}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all"
                    title="ערוך ישיבה"
                  >
                    <PencilIcon className="w-5 h-5" />
                    ערוך
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 shadow-lg hover:shadow-xl transition-all"
                  title="מחק ישיבה"
                >
                  <TrashIcon className="w-5 h-5" />
                  מחק
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Meeting Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-start gap-3">
              <UserGroupIcon className="w-6 h-6 text-blue-500 mt-1" />
              <div>
                <p className="text-sm text-gray-600">מנחה הישיבה</p>
                <p className="font-medium text-gray-900">{getUserName(meeting.facilitatorId)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-start gap-3">
              <DocumentTextIcon className="w-6 h-6 text-purple-500 mt-1" />
              <div>
                <p className="text-sm text-gray-600">מחלקה ראשית</p>
                <p className="font-medium text-gray-900">{getDepartmentName(meeting.mainDepartmentId)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-start gap-3">
              <CalendarIcon className="w-6 h-6 text-green-500 mt-1" />
              <div>
                <p className="text-sm text-gray-600">תאריך התחלה</p>
                <p className="font-medium text-gray-900 text-sm">{formatDate(meeting.startDate)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-start gap-3">
              <ClockIcon className="w-6 h-6 text-orange-500 mt-1" />
              <div>
                <p className="text-sm text-gray-600">משך הישיבה</p>
                <p className="font-medium text-gray-900">
                  {getMeetingDuration() || 'בתהליך'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Previous Meeting Link */}
        {meeting.previousMeetingId && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg shadow-lg p-4 mb-6 border-2 border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
                  <LinkIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-green-700 font-medium">ישיבת המשך של:</p>
                  <p className="font-bold text-green-900">
                    {meetings.find(m => m.id === meeting.previousMeetingId)?.subject || 'ישיבה קודמת'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/meetings/${meeting.previousMeetingId}`)}
                className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg font-medium"
              >
                צפה בישיבה הקודמת
              </button>
            </div>
          </div>
        )}

        {/* Participants */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <UserGroupIcon className="w-6 h-6" />
            משתתפים ({meeting.participantIds?.length || 0})
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {meeting.participantIds?.map(userId => (
              <div key={userId} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-medium">
                  {getUserName(userId).charAt(0)}
                </div>
                <span className="text-sm">{getUserName(userId)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Confirmation Status - Only for scheduled meetings */}
        {meeting.status === 'scheduled' && meeting.participantIds && meeting.participantIds.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
              סטטוס אישורי הגעה
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Confirmed */}
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5" />
                  אישרו הגעה ({meeting.confirmedParticipantIds?.length || 0})
                </h3>
                <div className="space-y-2">
                  {meeting.confirmedParticipantIds && meeting.confirmedParticipantIds.length > 0 ? (
                    meeting.confirmedParticipantIds.map(userId => (
                      <div key={userId} className="flex items-center gap-2 p-2 bg-white rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-medium text-sm">
                          {getUserName(userId).charAt(0)}
                        </div>
                        <span className="text-sm text-gray-900">{getUserName(userId)}</span>
                        <CheckCircleIcon className="w-4 h-4 text-green-600 mr-auto" />
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-green-700">אף אחד לא אישר עדיין</p>
                  )}
                </div>
              </div>

              {/* Pending */}
              <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                <h3 className="font-bold text-yellow-900 mb-3 flex items-center gap-2">
                  <ClockIcon className="w-5 h-5" />
                  ממתינים לאישור ({(meeting.participantIds?.length || 0) - (meeting.confirmedParticipantIds?.length || 0)})
                </h3>
                <div className="space-y-2">
                  {meeting.participantIds
                    .filter(userId => !meeting.confirmedParticipantIds || !meeting.confirmedParticipantIds.includes(userId))
                    .map(userId => (
                      <div key={userId} className="flex items-center gap-2 p-2 bg-white rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-yellow-500 text-white flex items-center justify-center font-medium text-sm">
                          {getUserName(userId).charAt(0)}
                        </div>
                        <span className="text-sm text-gray-900">{getUserName(userId)}</span>
                        <ClockIcon className="w-4 h-4 text-yellow-600 mr-auto" />
                      </div>
                    ))}
                  {(meeting.participantIds?.length || 0) === (meeting.confirmedParticipantIds?.length || 0) && (
                    <p className="text-sm text-yellow-700">כולם אישרו!</p>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Statistics */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">אחוז אישורים</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {meeting.participantIds && meeting.participantIds.length > 0
                      ? Math.round(((meeting.confirmedParticipantIds?.length || 0) / meeting.participantIds.length) * 100)
                      : 0}%
                  </p>
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-600 mb-1">סה"כ מוזמנים</p>
                  <p className="text-2xl font-bold text-blue-900">{meeting.participantIds?.length || 0}</p>
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-600 mb-1">אישרו</p>
                  <p className="text-2xl font-bold text-green-600">{meeting.confirmedParticipantIds?.length || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('summary')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'summary'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                סיכום ופרטים
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'tasks'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                משימות ({(createdTasks.length + linkedTasks.length)})
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'projects'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                פרויקטים ({(createdProjects.length + linkedProjects.length)})
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Summary Tab */}
            {activeTab === 'summary' && (
              <div className="space-y-6">
                {/* Agenda */}
                {meeting.agendaItems && meeting.agendaItems.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <ListBulletIcon className="w-5 h-5" />
                      אג'נדה
                    </h3>
                    <ul className="space-y-2">
                      {meeting.agendaItems.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </span>
                          <span className="flex-1">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Decisions */}
                {meeting.decisions && meeting.decisions.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <CheckCircleIcon className="w-5 h-5" />
                      החלטות מנהליות
                    </h3>
                    <ul className="space-y-2">
                      {meeting.decisions.map((decision, index) => (
                        <li key={index} className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
                          <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="flex-1">{decision}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Summary */}
                {meeting.summary && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <DocumentTextIcon className="w-5 h-5" />
                      סיכום
                    </h3>
                    <div className="p-4 bg-gray-50 rounded-lg whitespace-pre-wrap">
                      {meeting.summary}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <div className="space-y-6">
                {/* Created Tasks */}
                {createdTasks.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">משימות שנוצרו בישיבה</h3>
                    <div className="space-y-2">
                      {createdTasks.map(task => (
                        <div
                          key={task.id}
                          className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => navigate(`/tasks/${task.id}`)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">{task.title}</h4>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              task.status === 'הושלם' ? 'bg-green-100 text-green-800' :
                              task.status === 'במסלול' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {task.status}
                            </span>
                          </div>
                          {task.description && (
                            <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                          )}
                          <div className="flex gap-4 text-xs text-gray-500">
                            {task.assigneeIds && task.assigneeIds.length > 0 && (
                              <span>אחראים: {task.assigneeIds.map(getUserName).join(', ')}</span>
                            )}
                            {task.deadline && (
                              <span>דדליין: {new Date(task.deadline).toLocaleDateString('he-IL')}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked Tasks */}
                {linkedTasks.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <LinkIcon className="w-5 h-5" />
                      משימות קשורות
                    </h3>
                    <div className="space-y-2">
                      {linkedTasks.map(task => (
                        <div
                          key={task.id}
                          className="p-4 border border-purple-200 bg-purple-50 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => navigate(`/tasks/${task.id}`)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">{task.title}</h4>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              task.status === 'הושלם' ? 'bg-green-100 text-green-800' :
                              task.status === 'במסלול' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {task.status}
                            </span>
                          </div>
                          {task.description && (
                            <p className="text-sm text-gray-600">{task.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {createdTasks.length === 0 && linkedTasks.length === 0 && (
                  <p className="text-gray-500 text-center py-8">אין משימות הקשורות לישיבה זו</p>
                )}
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <div className="space-y-6">
                {/* Created Projects */}
                {createdProjects.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">פרויקטים שנוצרו בישיבה</h3>
                    <div className="space-y-2">
                      {createdProjects.map(project => (
                        <div
                          key={project.id}
                          className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => navigate(`/projects/${project.id}`)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">{project.name}</h4>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              project.status === 'הושלם' ? 'bg-green-100 text-green-800' :
                              project.status === 'במסלול' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {project.status}
                            </span>
                          </div>
                          {project.description && (
                            <p className="text-sm text-gray-600 mb-2">{project.description}</p>
                          )}
                          <div className="flex gap-4 text-xs text-gray-500">
                            {project.managerId && (
                              <span>מנהל: {getUserName(project.managerId)}</span>
                            )}
                            {project.teamMembers && project.teamMembers.length > 0 && (
                              <span>צוות: {project.teamMembers.length} חברים</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked Projects */}
                {linkedProjects.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <LinkIcon className="w-5 h-5" />
                      פרויקטים קשורים
                    </h3>
                    <div className="space-y-2">
                      {linkedProjects.map(project => (
                        <div
                          key={project.id}
                          className="p-4 border border-purple-200 bg-purple-50 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => navigate(`/projects/${project.id}`)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">{project.name}</h4>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              project.status === 'הושלם' ? 'bg-green-100 text-green-800' :
                              project.status === 'במסלול' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {project.status}
                            </span>
                          </div>
                          {project.description && (
                            <p className="text-sm text-gray-600">{project.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {createdProjects.length === 0 && linkedProjects.length === 0 && (
                  <p className="text-gray-500 text-center py-8">אין פרויקטים הקשורים לישיבה זו</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingViewer;

